import axiosFactory from "./axios.factory";

// Duenner Wrapper um die Retouren-Endpunkte plus eine sequentielle Upload-Queue:
// im Lager-WLAN laufen Uploads nacheinander (parallele Timeouts vermeiden) und
// werden bei Fehlern mit Backoff wiederholt.

const RETRY_DELAYS = [1000, 3000, 9000];

class ReturnsService {
  async list(params) {
    const res = await axiosFactory().get("/returns", { params });
    return res.data || {};
  }

  // Benutzerlisten fuer die Filter "Angelegt von"/"Erledigt von" (P20.4)
  async participants() {
    const res = await axiosFactory().get("/returns/participants");
    return res.data.data;
  }

  async get(id) {
    const res = await axiosFactory().get(`/returns/${id}`);
    return res.data.data;
  }

  async create(returnType) {
    const res = await axiosFactory().post("/returns", { returnType });
    return res.data.data;
  }

  async update(id, payload) {
    await axiosFactory().put(`/returns/${id}`, payload);
  }

  async photoTypes() {
    const res = await axiosFactory().get("/returns/phototypes");
    return res.data.data || [];
  }

  async photos(id) {
    const res = await axiosFactory().get(`/returns/${id}/photos`);
    return res.data.data || [];
  }

  async markNotAvailable(id, photoTypeId) {
    const res = await axiosFactory().post(`/returns/${id}/photos/not_available`, { photoTypeId });
    return res.data.data;
  }

  async deletePhoto(id, photoId) {
    await axiosFactory().delete(`/returns/${id}/photos/${photoId}`);
  }

  photoContentUrl(id, photoId) {
    return axiosFactory()
      .get(`/returns/${id}/photos/${photoId}/content`, { responseType: "blob" })
      .then((res) => URL.createObjectURL(new Blob([res.data])));
  }

  /**
   * Foto per Vision-Modell lesen. kind: "label" (Versandlabel) oder "ean" (EAN-Sticker).
   */
  async extract(id, kind) {
    const res = await axiosFactory().post(`/returns/${id}/extract`, {}, { params: kind ? { kind } : undefined, timeout: 180000 });
    return res.data.data;
  }

  /** Gescannten Lagerplatz-Barcode pruefen: Format + Existenz im WMS. */
  async storageLocation(code) {
    const res = await axiosFactory().get("/returns/storage_location", { params: { code } });
    return res.data.data;
  }

  /** Bereits erfasste Retouren zur selben Sendungsnummer. */
  async duplicates(trackingNumber, excludeReturnId) {
    const res = await axiosFactory().get("/returns/duplicates", { params: { trackingNumber, excludeReturnId } });
    return res.data.data || [];
  }

  async candidates(id, params) {
    const res = await axiosFactory().get(`/returns/${id}/candidates`, { params });
    return res.data.data || { candidates: [], diagnosis: [] };
  }

  async assign(id, payload) {
    await axiosFactory().post(`/returns/${id}/assign`, payload);
  }

  async setItems(id, items) {
    await axiosFactory().put(`/returns/${id}/items`, items);
  }

  async completeCapture(id) {
    await axiosFactory().post(`/returns/${id}/complete_capture`);
  }

  async bookingPreview(id) {
    const res = await axiosFactory().get("/returns/booking_locations", { params: { returnId: id } });
    return res.data.data || { locations: [] };
  }

  async book(id, items) {
    await axiosFactory().post(`/returns/${id}/book`, { items: items || [] });
  }

  /** Endstatus "Erledigt" - manuell, auch ohne Einbuchen. */
  async complete(id) {
    await axiosFactory().post(`/returns/${id}/complete`);
  }

  async savePhotoType(model) {
    const res = await axiosFactory().post("/returns/phototypes", model);
    return res.data.data;
  }

  async allPhotoTypes() {
    const res = await axiosFactory().get("/returns/phototypes", { params: { includeInactive: true } });
    return res.data.data || [];
  }

  /**
   * Foto mit Retry hochladen. onState meldet "uploading" | "done" | "error" (+ Versuchszaehler).
   */
  async uploadPhoto(id, photoTypeId, file, onState) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("photoTypeId", photoTypeId);

    for (let attempt = 0; ; attempt++) {
      try {
        if (onState) onState("uploading", attempt);
        const res = await axiosFactory().post(`/returns/${id}/photos`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000,
        });
        if (onState) onState("done", attempt);
        return res.data.data;
      } catch (err) {
        // Fachliche Fehler (400) nicht wiederholen - die Datei wird nicht besser
        const reason = err.response && err.response.data && err.response.data.reason;
        if (reason || attempt >= RETRY_DELAYS.length) {
          if (onState) onState("error", attempt, reason || "upload_failed");
          throw err;
        }
        await new Promise((r) => setTimeout(r, RETRY_DELAYS[attempt]));
      }
    }
  }
}

export default new ReturnsService();
